<template>
    <div data-widget="form-module" class="form-container">
        <form
            :id="`form-${formID}`"
            @submit.prevent="sendForm(formID)"
        >
            <div class="main-fields">
                <div
                    v-for="(field, fieldID) in form.children"
                    :key="fieldID"
                    class="col-span-full"
                    :class="{
                        'md:col-span-11': (field.columnWidth / 100 * 12) === 11,
                        'md:col-span-10': (field.columnWidth / 100 * 12) === 10,
                        'md:col-span-9': (field.columnWidth / 100 * 12) === 9,
                        'md:col-span-8': (field.columnWidth / 100 * 12) === 8,
                        'md:col-span-7': (field.columnWidth / 100 * 12) === 7,
                        'md:col-span-6': (field.columnWidth / 100 * 12) === 6,
                        'md:col-span-5': (field.columnWidth / 100 * 12) === 5,
                        'md:col-span-4': (field.columnWidth / 100 * 12) === 4,
                        'md:col-span-3': (field.columnWidth / 100 * 12) === 3
                    }"
                >
                    <span
                        v-if="field.type === 'Text' || field.type === 'Email'" 
                        class="text-input-container"
                    >
                        <input
                            :id="`${fieldID}`"
                            :name="field[langLabel]"
                            class="text-input" 
                            :type="field.type.toLowerCase()" 
                            :required = field.required
                            placeholder=""
                        >
                        <label :for="`${fieldID}`" class="text-label">{{ field[langLabel] }}<span v-if="field.required">*</span></label>

                        <span class="error-message"></span>
                    </span>

                    <span
                        v-if="field.type === 'Textarea'" 
                        class="text-input-container"
                    >
                        <textarea
                            :id="`${fieldID}`"
                            :name="field[langLabel]"
                            class="textarea text-input" 
                            type="text" 
                            :required = field.required
                            placeholder=""
                        />

                        <label :for="`${fieldID}`" class="text-label">{{ field[langLabel] }}<span v-if="field.required">*</span></label>

                        <span class="error-message"></span>
                    </span>

                    <div v-if="field.type === 'Checkbox'" class="checkbox">
                        <input
                            type="checkbox"
                            :id="`${fieldID}`"
                            :name="field[langLabel]"
                            :required="true"
                        >

                        <label :for="`${fieldID}`"><div class="inline">{{ field[langLabel] }}</div><span v-if="field.required">*</span></label>

                        <span class="error-message"></span>
                    </div>

                    <div
                        v-if="field.type === 'FormBuilderFile'"
                        class="file-upload-container"
                    >
                        <label :for="`${fieldID}`">{{ field[langLabel] }}</label>

                        <input
                            class="file-upload"
                            type="file"
                            :id="`${fieldID}`"
                            :name="field[langLabel]"
                            :accept="`.${field.extensions.replaceAll(' ', ', .')}`"
                        >

                        <span class="error-message"></span>
                    </div>
                </div>
            </div>

            <div class="form-legals">
                <div class="checkbox">
                    <input
                        type="checkbox"
                        id="legal-checkbox"
                        :name="'agb akzeptiert'"
                        :required="true"
                    >

                    <label :for="'legal-checkbox'"><div class="inline" v-html="globals.pagecontent.formTextLegals"></div><span>*</span></label>

                    <span class="error-message"></span>
                </div>
            </div>

            <div class="candy-bag">
                <input type="text" id="input-chocolate-chips">
                <input type="text" id="input-gummy-bears" value="this-is-fine">
            </div>

            <div class="form-submit">
                <FadeInTransition>
                    <button
                        v-show="!showPreloader"
                        class="button cta--default flex-row inline-flex"
                        :class="{'disabled': formSent}"
                    >
                        <span class="label">{{ globals.pagecontent.submitForm }}</span>
                        <span class="basis-auto">
                            <div class="circle circle-default"></div>
                        </span>
                    </button>
                </FadeInTransition>

                <FadeInTransition>
                    <span 
                        v-if="formSent && !showPreloader" 
                        class="success-msg col-span-full block mt-1"
                    >{{ form[langSuccess] }}</span>
                </FadeInTransition>

                <FadeInTransition>
                    <PreloaderIcon v-if="showPreloader" />
                </FadeInTransition>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
    import { useRequestStore } from '@/stores/requests'
    import { type AuthResponse } from '@/types/AuthTypes'
    import { nanoid } from 'nanoid'
    import { FormHandler } from "~/composables/Classes/FormHandler"

    const props = defineProps<{
        pID?: number,
        givenFormID?: number
    }>()

    const { locale } = useI18n()

    const requestStore = useRequestStore()
    const { pageContent, pageID, form, pageIndex, globals } = storeToRefs(requestStore)

    const showPreloader = ref<boolean>(false)
    const formSent = ref<boolean>(false)
    const formID = ref<number>()

    const getForm = async (formID: any) => {
        try {
            const formData = await useBackendApiGet<AuthResponse>(`v1/form-data/${formID}/${locale.value}`);

            if (formData) {
                requestStore.fillPageform(formData)
            }
        } catch (error) {
            console.error(error)
        }
    }

    const initForm = () => {
        if (props.givenFormID) {
            formID.value = props.givenFormID
        } else {
            const widgets = pageContent.value[pageID.value]?.pagecontent.widgets
            const widgetContent = widgets?.find((widget: any) => widget.meta.cID === props.pID)
            formID.value = widgetContent.meta.form[0].formID
        }

        getForm(formID.value)
    }

    const langLabel = computed(() => {
        const langID = pageIndex.value.global_meta.langID
        const label = langID === 1017 ? 'label' : `label${langID}`

        return label
    })

    const langSuccess = computed(() => {
        const langID = pageIndex.value.global_meta.langID
        const successMsg = langID === 1017 ? 'successMessage' : `successMessage${langID}`

        return successMsg
    })

    const sendForm = async (formID: any) => {
        const form: any = document.querySelector('#form-' + formID)
        const elements = form.elements
        const elementsArray = Array.prototype.slice.call(elements)
        
        showPreloader.value = true

        const jsonData: any = {}

        elementsArray.forEach(element => {
            switch (element.type) {
                case 'email':
                case 'text':
                case 'textarea':
                    jsonData[element.id] = element.value
                    break
                case 'checkbox':
                    jsonData[element.id] = element.checked
                    break
                case 'submit':
                    break
            }
        })

        FormHandler.validateOnSubmit(elementsArray)

        if(form.getElementsByClassName('input-error').length > 0) {
            showPreloader.value = false
            return false
        }

        jsonData['messageID'] = nanoid(6)
        jsonData['formID'] = formID;

        const formStatus = await FormHandler.sendFormData(jsonData, formID, locale.value)

        switch (formStatus) {
            case '200':
                formSent.value = true
                
                setTimeout(() => {
                    showPreloader.value = false
                }, 2000)
                break;
            case '400':
            case '404':
            case '405':
            case '500':
                form.querySelector('.form-submit button').classList.remove('disabled')
                form.querySelector('.form-submit button').classList.add('error')
                break;
        }
    }

    initForm()
</script>